import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { authAPI } from '../services/api';
import { ShieldCheck, KeyRound, Copy, Check } from 'lucide-react';

// FASE A — Seguridad de la cuenta: cambio obligatorio de contraseña y alta de 2FA (TOTP).
const CuentaSeguridadPage = () => {
  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [repetir, setRepetir] = useState('');
  const [cambiada, setCambiada] = useState(false);
  const [setup, setSetup] = useState(null);
  const [codigo, setCodigo] = useState('');
  const [activo, setActivo] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [cargando, setCargando] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const cambiar = async (e) => {
    e.preventDefault();
    if (nueva !== repetir) {
      toast({ title: 'Las contraseñas no coinciden', variant: 'destructive' });
      return;
    }
    setCargando(true);
    try {
      const r = await authAPI.changePasswordRequired(actual, nueva);
      if (r.access_token) localStorage.setItem('upim_token', r.access_token);
      setCambiada(true);
      toast({ title: 'Contraseña actualizada', description: 'La contraseña temporal quedó invalidada.' });
    } catch (error) {
      toast({ title: 'No se pudo cambiar', description: error.message, variant: 'destructive' });
    } finally {
      setCargando(false);
    }
  };

  const iniciar2FA = async () => {
    setCargando(true);
    try {
      setSetup(await authAPI.totpSetup());
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setCargando(false);
    }
  };

  const activar2FA = async (e) => {
    e.preventDefault();
    setCargando(true);
    try {
      await authAPI.totpEnable(codigo);
      setActivo(true);
      toast({ title: '2FA activado', description: 'Tu cuenta ya exige verificación en dos pasos.' });
    } catch (error) {
      toast({ title: 'Código incorrecto', description: error.message, variant: 'destructive' });
    } finally {
      setCargando(false);
    }
  };

  const copiarCodigos = async () => {
    await navigator.clipboard.writeText((setup?.backup_codes || []).join('\n'));
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2500);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto space-y-6" data-testid="cuenta-seguridad">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Seguridad de la cuenta</h1>
          <p className="text-base text-gray-600">
            Define tu contraseña definitiva y activa la verificación en dos pasos.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <KeyRound className="h-5 w-5" /> 1. Cambio de contraseña obligatorio
            </CardTitle>
          </CardHeader>
          <CardContent>
            {cambiada ? (
              <p data-testid="password-cambiada" className="text-sm text-green-700">
                Contraseña actualizada. La contraseña temporal ya no funciona y las sesiones
                anteriores fueron invalidadas.
              </p>
            ) : (
              <form onSubmit={cambiar} className="space-y-4" data-testid="form-cambio-password">
                <div>
                  <Label>Contraseña temporal actual</Label>
                  <Input type="password" value={actual} onChange={(e) => setActual(e.target.value)}
                    required data-testid="password-actual" />
                </div>
                <div>
                  <Label>Nueva contraseña</Label>
                  <Input type="password" value={nueva} onChange={(e) => setNueva(e.target.value)}
                    required data-testid="password-nueva" />
                  <p className="text-xs text-gray-500 mt-1">
                    Mínimo 12 caracteres, con mayúsculas, minúsculas y al menos un número.
                  </p>
                </div>
                <div>
                  <Label>Repetir nueva contraseña</Label>
                  <Input type="password" value={repetir} onChange={(e) => setRepetir(e.target.value)}
                    required data-testid="password-repetir" />
                </div>
                <Button type="submit" disabled={cargando} className="bg-gray-900"
                  data-testid="password-guardar">Actualizar contraseña</Button>
              </form>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <ShieldCheck className="h-5 w-5" /> 2. Verificación en dos pasos (obligatoria)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {activo ? (
              <div className="space-y-3">
                <p data-testid="totp-activo" className="text-sm text-green-700">
                  2FA activo. En cada inicio de sesión se pedirá el código de tu aplicación.
                </p>
                <Button onClick={() => navigate('/admin/afiliaciones')} className="bg-gray-900"
                  data-testid="ir-al-panel">Ir al panel de afiliaciones</Button>
              </div>
            ) : !setup ? (
              <Button onClick={iniciar2FA} disabled={cargando || !cambiada} className="bg-gray-900"
                data-testid="totp-iniciar">
                {cambiada ? 'Configurar 2FA' : 'Primero cambia tu contraseña'}
              </Button>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-700">
                  Escanea el código con Google Authenticator, Microsoft Authenticator o 1Password.
                </p>
                <img data-testid="totp-qr" alt="Código QR para 2FA"
                  src={`data:image/png;base64,${setup.qr_png_base64}`}
                  className="w-44 h-44 border rounded-lg bg-white p-2" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    Códigos de respaldo (se muestran una sola vez)
                  </p>
                  <div data-testid="totp-backup-codes"
                    className="grid grid-cols-2 gap-2 mt-2 text-xs font-mono bg-gray-100 rounded p-3">
                    {setup.backup_codes.map((c) => <span key={c}>{c}</span>)}
                  </div>
                  <Button variant="outline" size="sm" className="mt-2" onClick={copiarCodigos}
                    data-testid="totp-copiar-codigos">
                    {copiado ? <Check className="h-4 w-4 mr-2 text-green-600" /> : <Copy className="h-4 w-4 mr-2" />}
                    {copiado ? 'Copiados' : 'Copiar códigos'}
                  </Button>
                </div>
                <form onSubmit={activar2FA} className="flex items-end gap-3" data-testid="form-totp">
                  <div>
                    <Label>Código de 6 dígitos</Label>
                    <Input value={codigo} onChange={(e) => setCodigo(e.target.value)} maxLength={6}
                      inputMode="numeric" placeholder="000000" data-testid="totp-codigo" />
                  </div>
                  <Button type="submit" disabled={cargando} className="bg-gray-900"
                    data-testid="totp-activar">Activar 2FA</Button>
                </form>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CuentaSeguridadPage;
